import  { useCallback, useMemo } from 'react';
import { Input, LoadingSpinner } from '../atoms';
import { TableHeader, TableRow, TablePagination } from '../molecues';
import { useTable } from '../../hooks/useTable';
import type { DataTableProps, SortDirection } from '../../types/table';

/**
 * DataTable Component - Organism Level
 * 
 * Comprehensive server-side table component with full features
 * 
 * Features:
 * - Server-side pagination
 * - Server-side sorting
 * - Server-side filtering
 * - Search functionality
 * - Row selection
 * - Action buttons
 * - Loading states
 * - Empty states
 * - Responsive design
 * - Accessible (ARIA, keyboard navigation)
 * - Performance optimized (memoization, debouncing)
 * 
 * @example
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   fetchData={fetchProducts}
 *   searchable
 *   rowSelection={{ enabled: true }}
 *   actions={[
 *     { id: 'edit', label: 'Edit', onClick: handleEdit },
 *     { id: 'delete', label: 'Delete', variant: 'danger', onClick: handleDelete },
 *   ]}
 * />
 * ```
 */

export const DataTable = <TData extends Record<string, any>>({
  columns,
  fetchData,
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  searchable = false,
  searchPlaceholder = 'Search...',
  searchDebounce = 300,
  rowSelection,
  actions = [],
  loading: loadingConfig,
  emptyState: emptyStateConfig,
  className = '',
  tableClassName = '',
  hoverable = true,
  striped = false,
  caption,
  onRowClick,
  getRowClassName,
  refreshInterval = 0,
  showRefreshButton = false,
  onRefresh,
  renderHeaderActions,
}: DataTableProps<TData>) => {

  const {
    data,
    state,
    loading,
    error,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    setFilter,
    removeFilter,
    clearFilters,
    refresh,
    toggleRowSelection,
    selectAllRows,
    clearSelection,
  } = useTable<TData>(fetchData, {
    initialPageSize,
    refreshInterval,
    searchDebounce,
  });


  const hasSelection = rowSelection?.enabled ?? false;
  const hasActions = actions.length > 0;
  const allSelected = useMemo(
    () => data.length > 0 && data.every((_, idx) => state.selectedRows.has(idx)),
    [data, state.selectedRows]
  );

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback(
    (columnId: string) => {
      const currentSort = state.sort;
      let newDirection: SortDirection = 'asc';

      if (currentSort?.columnId === columnId) {
        // Cycle through: asc -> desc -> null
        if (currentSort.direction === 'asc') {
          newDirection = 'desc';
        } else if (currentSort.direction === 'desc') {
          newDirection = null;
        }
      }

      setSort(columnId, newDirection);
    },
    [state.sort, setSort]
  );

  /**
   * Handle select all toggle
   */
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAllRows();
    }
  }, [allSelected, clearSelection, selectAllRows]);

  /**
   * Handle row selection toggle
   */
  const handleRowSelection = useCallback(
    (index: number) => {
      toggleRowSelection(index);
      if (rowSelection?.onSelectionChange) {
        // Get selected row IDs after toggle
        const selectedIds = Array.from(state.selectedRows);
        rowSelection.onSelectionChange(selectedIds);
      }
    },
    [toggleRowSelection, rowSelection, state.selectedRows]
  );

  /**
   * Handle refresh button click
   */
  const handleRefresh = useCallback(() => {
    refresh();
    onRefresh?.();
  }, [refresh, onRefresh]);

  // ============================================================================
  // Rendering Helpers
  // ============================================================================

  /**
   * Render loading state
   */
  const renderLoading = () => {
    const colSpan = columns.length + (hasSelection ? 1 : 0) + (hasActions ? 1 : 0);
    
    if (loadingConfig?.loadingComponent) {
      return (
        <tr>
          <td colSpan={colSpan} className="px-6 py-12">
            {loadingConfig.loadingComponent}
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-12">
          <div className="flex flex-col items-center justify-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-sm text-gray-600">
              {loadingConfig?.loadingText || 'Loading data...'}
            </p>
          </div>
        </td>
      </tr>
    );
  };

  /**
   * Render empty state
   */
  const renderEmpty = () => {
    const colSpan = columns.length + (hasSelection ? 1 : 0) + (hasActions ? 1 : 0);
    
    if (emptyStateConfig?.emptyComponent) {
      return (
        <tr>
          <td colSpan={colSpan} className="px-6 py-12">
            {emptyStateConfig.emptyComponent}
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-12">
          <div className="flex flex-col items-center justify-center">
            {emptyStateConfig?.emptyIcon || (
              <svg
                className="w-16 h-16 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            )}
            <p className="mt-4 text-sm font-medium text-gray-900">
              {emptyStateConfig?.emptyMessage || 'No data available'}
            </p>
          </div>
        </td>
      </tr>
    );
  };

  /**
   * Render error state
   */
  const renderError = () => {
    const colSpan = columns.length + (hasSelection ? 1 : 0) + (hasActions ? 1 : 0);
    
    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-12">
          <div className="flex flex-col items-center justify-center">
            <svg
              className="w-16 h-16 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-4 text-sm font-medium text-gray-900">Failed to load data</p>
            <p className="mt-1 text-sm text-gray-600">{error?.message}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 text-sm text-[#EAB308] hover:text-[#CA8A04] font-medium"
            >
              Try again
            </button>
          </div>
        </td>
      </tr>
    );
  };

  // Main Render

  return (
    <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${className}`}>
      {/* Header Section */}
      {(searchable || showRefreshButton || renderHeaderActions) && (
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="flex items-center justify-between gap-4">
            {/* Search */}
            {searchable && (
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  onChange={(e) => setSearch(e.target.value)}
                  leftIcon={
                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                />
              </div>
            )}

            {/* Refresh Button */}
            {showRefreshButton && (
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 disabled:opacity-50 transition-colors"
              >
                <svg
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </button>
          )}
          </div>

          {/* Custom Header Actions (Filters) */}
          {renderHeaderActions && renderHeaderActions({
            setFilter,
            removeFilter,
            clearFilters,
            filters: state.filters,
          })}
        </div>
      )}

      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className={`w-full ${tableClassName}`}>
          {caption && <caption className="sr-only">{caption}</caption>}
          
          <TableHeader
            columns={columns}
            sortState={state.sort}
            onSortChange={handleSortChange}
            hasSelection={hasSelection}
            allSelected={allSelected}
            onSelectAll={handleSelectAll}
            hasActions={hasActions}
          />

          <tbody className="bg-white divide-y divide-gray-200">
            {loading && renderLoading()}
            
            {!loading && error && renderError()}
            
            {!loading && !error && data.length === 0 && renderEmpty()}
            
            {!loading &&
              !error &&
              data.length > 0 &&
              data.map((row, index) => (
                <TableRow
                  key={index}
                  row={row}
                  columns={columns}
                  index={index}
                  selected={state.selectedRows.has(index)}
                  onSelectionChange={() => handleRowSelection(index)}
                  hasSelection={hasSelection}
                  actions={actions}
                  onRowClick={onRowClick}
                  hoverable={hoverable}
                  striped={striped}
                  className={getRowClassName?.(row)}
                />
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && !error && data.length > 0 && (
        <div className="p-6 border-t border-gray-200">
          <TablePagination
            pagination={state.pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={pageSizeOptions}
            showPageSize
            showItemCount
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;

